export type CampaignGenerationCheck = {
  mode: "autopilot" | "on_demand";
  nextRunAt: string;
  status: "queued" | "processing";
};

export type PendingCampaignGeneration = {
  mode: CampaignGenerationCheck["mode"];
  status: CampaignGenerationCheck["status"];
};

export const getPendingCampaignGeneration = (input: {
  generationCheck: CampaignGenerationCheck | null;
  now: Date;
}): PendingCampaignGeneration | null => {
  if (!input.generationCheck) {
    return null;
  }

  if (input.generationCheck.status === "processing" || input.generationCheck.mode === "on_demand") {
    return {
      mode: input.generationCheck.mode,
      status: input.generationCheck.status,
    };
  }

  if (new Date(input.generationCheck.nextRunAt).getTime() <= input.now.getTime()) {
    return {
      mode: input.generationCheck.mode,
      status: input.generationCheck.status,
    };
  }

  return null;
};

export const getActionablePendingCampaignGeneration = (input: {
  generationCheck: CampaignGenerationCheck | null;
  now: Date;
  autopilotEnabled: boolean;
}): PendingCampaignGeneration | null => {
  const pendingGeneration = getPendingCampaignGeneration({
    generationCheck: input.generationCheck,
    now: input.now,
  });

  if (!input.autopilotEnabled && pendingGeneration?.mode === "autopilot") {
    return null;
  }

  return pendingGeneration;
};

export const getPendingCampaignGenerationRefetchInterval = (input: {
  generationCheck: CampaignGenerationCheck | null;
  now: Date;
  intervalMs: number;
  autopilotEnabled?: boolean;
}): number | false => {
  return getActionablePendingCampaignGeneration({
    generationCheck: input.generationCheck,
    now: input.now,
    autopilotEnabled: input.autopilotEnabled ?? true,
  })
    ? input.intervalMs
    : false;
};

export type CampaignBucket = "needs_review" | "active" | "drafts" | "complete";
export type CampaignFilter = "all" | CampaignBucket;

export type CampaignViewSlot = {
  scheduledFor: string;
  status: string;
  approvalStatus?: string | null;
  platform?: string | null;
};

export type CampaignViewItem = {
  status: string;
  startDate: string;
  endDate: string;
  scheduledPosts: CampaignViewSlot[];
  review: {
    totalSlots: number;
    readySlots: number;
    inFlightSlots: number;
    failedSlots: number;
  };
};

export type CampaignTimelineRange = {
  start: Date;
  end: Date;
  totalDays: number;
};

export type CampaignTimelinePositionedDate = {
  date: Date;
  leftPercent: number;
};

const MIN_TIMELINE_MONTH_LABEL_SPACING_PERCENT = 10;

export const CAMPAIGN_BUCKETS = ["needs_review", "active", "drafts", "complete"] as const;
export const CAMPAIGN_FILTERS = ["all", ...CAMPAIGN_BUCKETS] as const;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const startOfUtcDay = (date: Date): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

const addDays = (date: Date, days: number): Date =>
  new Date(startOfUtcDay(date).getTime() + days * MS_PER_DAY);

const daysBetween = (start: Date, end: Date): number =>
  Math.round((startOfUtcDay(end).getTime() - startOfUtcDay(start).getTime()) / MS_PER_DAY);

export const getCampaignBucket = (campaign: Pick<CampaignViewItem, "status">): CampaignBucket => {
  switch (campaign.status) {
    case "pending_review":
      return "needs_review";
    case "draft":
      return "drafts";
    case "completed":
      return "complete";
    default:
      return "active";
  }
};

export const getCampaignBucketLabel = (bucket: CampaignBucket): string => {
  switch (bucket) {
    case "needs_review":
      return "Needs review";
    case "active":
      return "Active";
    case "drafts":
      return "Draft";
    case "complete":
      return "Complete";
  }
};

export const getCampaignFilterLabel = (filter: CampaignFilter): string => {
  if (filter === "all") {
    return "All";
  }

  return getCampaignBucketLabel(filter);
};

export const getCampaignBucketCounts = (campaigns: CampaignViewItem[]) => {
  const counts: Record<CampaignBucket, number> = {
    needs_review: 0,
    active: 0,
    drafts: 0,
    complete: 0,
  };

  for (const campaign of campaigns) {
    counts[getCampaignBucket(campaign)] += 1;
  }

  return counts;
};

export const getCampaignFilterCount = (
  campaigns: CampaignViewItem[],
  filter: CampaignFilter,
): number => {
  if (filter === "all") {
    return campaigns.length;
  }

  return getCampaignBucketCounts(campaigns)[filter];
};

export const filterCampaignsByBucket = <TCampaign extends CampaignViewItem>(
  campaigns: TCampaign[],
  filter: CampaignFilter,
): TCampaign[] =>
  filter === "all"
    ? campaigns
    : campaigns.filter((campaign) => getCampaignBucket(campaign) === filter);

export const getCampaignListMetrics = (campaign: CampaignViewItem) => {
  const hasApprovalStatus = campaign.scheduledPosts.some((slot) => slot.approvalStatus);
  const approvedSlots = campaign.scheduledPosts.filter(
    (slot) => slot.approvalStatus === "approved",
  ).length;
  const pendingSlots = campaign.scheduledPosts.filter(
    (slot) => slot.status === "generated" && slot.approvalStatus !== "approved",
  ).length;

  return {
    assets: campaign.review.totalSlots,
    pending: hasApprovalStatus ? pendingSlots : campaign.review.readySlots,
    approved: approvedSlots,
  };
};

export const getCampaignChannelLabels = (campaign: CampaignViewItem): string[] => {
  const labels = campaign.scheduledPosts
    .map((slot) => slot.platform)
    .filter((platform): platform is string => Boolean(platform))
    .map((platform) =>
      platform
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" "),
    );

  return Array.from(new Set(labels.length > 0 ? labels : ["Instagram"]));
};

export const getCampaignTimelineRange = (input: {
  campaigns: CampaignViewItem[];
  now: Date;
  minDays?: number;
  paddingDays?: number;
}): CampaignTimelineRange => {
  const minDays = input.minDays ?? 35;
  const paddingDays = input.paddingDays ?? 4;
  const now = startOfUtcDay(input.now);
  const starts = input.campaigns.map((campaign) => startOfUtcDay(new Date(campaign.startDate)));
  const ends = input.campaigns.map((campaign) => startOfUtcDay(new Date(campaign.endDate)));
  const earliest = starts.length
    ? new Date(Math.min(now.getTime(), ...starts.map((date) => date.getTime())))
    : now;
  const latest = ends.length
    ? new Date(Math.max(now.getTime(), ...ends.map((date) => date.getTime())))
    : addDays(now, minDays);
  const start = addDays(earliest, -paddingDays);
  const paddedEnd = addDays(latest, paddingDays);
  const minimumEnd = addDays(start, minDays);
  const end = paddedEnd.getTime() >= minimumEnd.getTime() ? paddedEnd : minimumEnd;

  return {
    start,
    end,
    totalDays: Math.max(1, daysBetween(start, end)),
  };
};

export const getCampaignTimelineBar = (
  campaign: CampaignViewItem,
  range: CampaignTimelineRange,
) => {
  const campaignStart = startOfUtcDay(new Date(campaign.startDate));
  const campaignEnd = startOfUtcDay(new Date(campaign.endDate));
  const visibleStart =
    campaignStart.getTime() < range.start.getTime() ? range.start : campaignStart;
  const visibleEnd = campaignEnd.getTime() > range.end.getTime() ? range.end : campaignEnd;
  const leftDays = Math.max(0, daysBetween(range.start, visibleStart));
  const widthDays = Math.max(1, daysBetween(visibleStart, visibleEnd));

  return {
    leftPercent: (leftDays / range.totalDays) * 100,
    widthPercent: Math.min(100, (widthDays / range.totalDays) * 100),
  };
};

export const getCampaignTimelineTicks = (
  range: CampaignTimelineRange,
  intervalDays = 7,
): CampaignTimelinePositionedDate[] => {
  const ticks: CampaignTimelinePositionedDate[] = [];
  const safeIntervalDays = Math.max(1, intervalDays);

  for (let day = 0; day <= range.totalDays; day += safeIntervalDays) {
    ticks.push({
      date: addDays(range.start, day),
      leftPercent: (day / range.totalDays) * 100,
    });
  }

  return ticks;
};

export const getCampaignTimelineMonthLabels = (
  range: CampaignTimelineRange,
): CampaignTimelinePositionedDate[] => {
  const labels: CampaignTimelinePositionedDate[] = [];
  let cursor = new Date(Date.UTC(range.start.getUTCFullYear(), range.start.getUTCMonth(), 1));

  while (cursor.getTime() < range.end.getTime()) {
    const visibleDate = cursor.getTime() < range.start.getTime() ? range.start : cursor;
    const leftDays = daysBetween(range.start, visibleDate);

    const label = {
      date: cursor,
      leftPercent: (leftDays / range.totalDays) * 100,
    };
    const previous = labels.at(-1);

    if (
      previous &&
      label.leftPercent - previous.leftPercent < MIN_TIMELINE_MONTH_LABEL_SPACING_PERCENT
    ) {
      const previousIsPartialStart = previous.date.getTime() < range.start.getTime();
      const currentStartsInRange = cursor.getTime() >= range.start.getTime();

      if (previousIsPartialStart && currentStartsInRange) {
        labels[labels.length - 1] = label;
      }

      cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
      continue;
    }

    labels.push(label);

    cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
  }

  return labels;
};

export const getTodayMarkerPercent = (range: CampaignTimelineRange, now: Date): number | null => {
  const today = startOfUtcDay(now);
  if (today.getTime() < range.start.getTime() || today.getTime() > range.end.getTime()) {
    return null;
  }

  return (daysBetween(range.start, today) / range.totalDays) * 100;
};
